// ProcessSteps.js
import React from 'react';
import { motion } from 'framer-motion';

const ProcessSteps = () => {
  const steps = [
    {
      number: '01',
      title: 'Eligibility Consultation',
      subtitle: 'Everything starts here, with a conversation about eligibility.',
      points: [
        'We go through what you have done and what you hold',
        'We check whether your documents are in order and which roles suit you',
        'We set out what employers ask for and how long things take',
        'We tell you plainly how likely you are to move forward'
      ]
    },
    {
      number: '02',
      title: 'Placement into the Pipeline',
      subtitle: 'If you qualify, you are:',
      points: [
        'Added to the Ivipass Work Abroad pipeline',
        'Lined up against roles that fit your profile',
        'Told exactly what happens next and what to expect'
      ]
    },
    {
      number: '03',
      title: 'Interviews Arranged and Prepared For',
      subtitle: 'From that point onward we:',
      points: [
        'Arrange interview slots with the employers we partner with',
        'Pass on timings and details as soon as we have them',
        'Give you material to work through and guidance on using it',
        'Keep working with you until you are ready to be interviewed'
      ]
    },
    {
      number: '04',
      title: 'The Employer Decides',
      subtitle: 'Once the interview is done:',
      points: [
        'The result comes to you from the employer, not from us',
        'Any offer of employment is issued by the employer itself',
        'Contract, pay and terms are settled between you and them'
      ]
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-white text-secondary text-sm font-light px-6 py-2 rounded-full mb-4">
            The Process
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            How the Work Abroad Pipeline Runs
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Four stages, from the first eligibility conversation through to an employer decision
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative"
            >
              <div className="grid md:grid-cols-12 gap-6 items-start">
                {/* Left Side - Number Only */}
                <div className="md:col-span-2 flex items-center md:items-start">
                  <div className="flex-shrink-0">
                    <div className="text-6xl font-light text-primary/10">
                      {step.number}
                    </div>
                  </div>
                </div>

                {/* Right Side - Content */}
                <div className="md:col-span-10">
                  <div className="bg-white p-8 rounded-lg shadow-sm">
                    <h3 className="text-2xl font-semibold text-primary mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm font-light mb-4">
                      {step.subtitle}
                    </p>
                    
                    {/* Points List */}
                    <ul className="space-y-2">
                      {step.points.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span className="text-gray-600 text-sm font-light">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;
